import { useEffect, useState } from 'react';
import { GridInitialState } from '@mui/x-data-grid-pro';
import { getUserSubject } from '../../../../../store/User';
import {
  SupplierMap,
  fetchSupplierMaps,
  getTallySupplierMapsLoadingSubject,
  getTallySupplierMapsSubject
} from '../../../../../store/SupplierMap';
import { DefaultSupplierMapGridColumnDefs } from '../../ColumnDefs/SupplierMapGridColumnDefs';
import { UpstackDataGrid } from '../../../../Compounds/UpstackDataGrid/UpstackDataGrid';
import { getGridColumns, getInitialState } from '../../../../Compounds/UpstackDataGrid/helpers';
import { PageCard } from '../../../../Compounds/CardWithTitle';
import { getDefaultPreferences, GridPreferences } from '../../../../../store/UserCustomizations';
import { Icon } from '../../../../Atoms/Icon';
import { TextSpan } from '../../../../Atoms/Text';
import { GridCustomColDef } from '../../../../types';
import { ComponentSpinner } from '../../../../Compounds/Loading/ComponentSpinner';
import { NewMapDialog } from '../../../../Compounds/Mapping/NewMapDialog/NewMapDialog';
import { EntityType } from '../../../../../store/Search';
import { PageLayout } from '../../../../PageLayout';

export const SupplierMapTab = () => {
  const [supplierMapsLoading, setSupplierMapsLoading] = useState<boolean>(false);
  const [supplierMaps, setSupplierMaps] = useState<SupplierMap[]>([]);
  const [supplierMapColumns, setSupplierMapColumns] = useState<GridCustomColDef[]>(DefaultSupplierMapGridColumnDefs);
  const [isMapFormOpen, setIsMapFormOpen] = useState<boolean>(false);
  const [gridSettings, setGridSettings] = useState<GridPreferences>(
    getDefaultPreferences()?.content['supplierMapGrid'] as GridPreferences
  );
  const [initialState, setInitialState] = useState<GridInitialState>();

  useEffect(() => {
    const loadingSubscription = getTallySupplierMapsLoadingSubject().subscribe((loading) =>
      setSupplierMapsLoading(loading)
    );
    const supplierMapsSub = getTallySupplierMapsSubject().subscribe((maps) => setSupplierMaps(maps));
    const userSub = getUserSubject().subscribe((usr) => {
      if (usr?.preferences?.content?.supplierMapGrid) {
        setGridSettings(usr.preferences.content.supplierMapGrid);
        const state = getInitialState(
          usr.preferences.content.supplierMapGrid,
          DefaultSupplierMapGridColumnDefs,
          'supplierMapGrid'
        );
        setInitialState(state);
      }
    });

    fetchSupplierMaps();

    return () => {
      if (loadingSubscription) loadingSubscription.unsubscribe();
      if (supplierMapsSub) supplierMapsSub.unsubscribe();
      if (userSub) userSub.unsubscribe();
    };
  }, []);

  useEffect(() => {
    if (!supplierMapsLoading && supplierMaps.length > 0) {
      setSupplierMapColumns(getGridColumns(supplierMaps, DefaultSupplierMapGridColumnDefs, 'supplierMapGrid'));
    }
  }, [supplierMapsLoading, supplierMaps.length]);

  const handleNewMap = () => {
    setIsMapFormOpen(true);
  };

  const cardActions = () => (
    <button
      className="flex items-center space-x-2"
      onClick={handleNewMap}
      data-cy="new-supplier-map-button"
      data-testid="new-supplier-map-button">
      <Icon type="plus" />
      <TextSpan
        color="indigo"
        size="sm14">
        New Supplier Map
      </TextSpan>
    </button>
  );

  return (
    <PageLayout
      className="w-full"
      dataTestId="supplier-map-page"
      dataCy="supplier-map-page-card"
      pageTitle="">
      <PageCard
        dataTestId="supplier-map-management"
        dataCy="supplier-map-management"
        childrenWrapperClass="min-h-80 max-w-[60vw] min-w-full"
        title="Supplier Map"
        cardActions={cardActions()}>
        <>
          {!initialState ||
            (supplierMapsLoading && (
              <div className="flex h-[45rem] m-auto">
                <ComponentSpinner label="Loading data..." />
              </div>
            ))}
          {initialState && !supplierMapsLoading && (
            <UpstackDataGrid
              title="Supplier Map"
              rows={supplierMaps}
              columns={supplierMapColumns}
              page="supplierMapGrid"
              gridSettings={gridSettings}
              showSearch={true}
              initialState={initialState}
              apiFilter={false}
              sxStyles={{ borderWidth: '0', borderBottomLeftRadius: '.75rem', borderBottomRightRadius: '.75rem' }}
            />
          )}
          <NewMapDialog
            open={isMapFormOpen}
            setOpen={setIsMapFormOpen}
            mapType={EntityType.SUPPLIER}
          />
        </>
      </PageCard>
    </PageLayout>
  );
};
